import { TuiCardLarge } from '@taiga-ui/layout';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { AdministrationUser } from '@modules/administration/models/administration-user';
import { UserAdminState } from '@modules/administration/models/user-admin-state.enum';
import { AuthenticationService } from '@modules/authentication/services/authentication.service';
import { ActionButtonsComponent } from '@modules/shared/action-buttons/action-buttons.component';
import { Store } from '@ngrx/store';
import { LicenseState } from '@pixacare/domain';
import { UserCardComponent } from '@shared/components/user-card/user-card.component';
import { ActionsByUserState } from '@shared/models/actions-by-user-state';
import { ActionButton } from '@shared/models/menu-actions/action-buttons';
import { menuActionsToActionsButtons } from '@shared/models/menu-actions/adapters/menu-action.adapters';
import { SortPipe } from '@shared/pipes/sort.pipe';
import { selectHasLicenseSupportAccess } from '@shared/store/licenses/licenses.selectors';
import { TuiTable } from '@taiga-ui/addon-table';
import { TuiHint, TuiScrollable, TuiScrollbar } from '@taiga-ui/core';
import { TuiLineClamp, TuiSwitch } from '@taiga-ui/kit';
import { UserAdminStateBadgeComponent } from '../user-state-badge/user-admin-state-badge.component';
import { UsersAdminSortService } from './users-admin-sort.service';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';

@Component({
  selector: 'pxc-users-administration-table',
  imports: [
    EmptyStateComponent,
    ActionButtonsComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ScrollingModule,
    SortPipe,
    TranslocoDirective,
    TuiTable,
    TuiScrollable,
    TuiScrollbar,
    TuiSwitch,
    TuiCardLarge,
    TuiLineClamp,
    TuiHint,
    UserCardComponent,
    UserAdminStateBadgeComponent,
  ],
  providers: [UsersAdminSortService],
  templateUrl: './users-administration-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersAdministrationTableComponent {
  readonly administrationUsers = input.required<AdministrationUser[]>();
  readonly actionsByUserState = input.required<ActionsByUserState>();
  readonly showDepartmentNames = input(false);

  readonly adminChanged = output<AdministrationUser>();

  readonly usersAdminSortService = inject(UsersAdminSortService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly store = inject(Store);

  private readonly hasSupportLicense = this.store.selectSignal(selectHasLicenseSupportAccess);
  readonly currentUserId: number = this.authenticationService.user.id;
  readonly canShowDepartmentNames = computed(() => this.hasSupportLicense() && this.showDepartmentNames());

  readonly columns = computed(() =>
    this.canShowDepartmentNames()
      ? ['name', 'state', 'groups', 'admin', 'actions']
      : ['name', 'state', 'admin', 'actions'],
  );

  readonly TABLE_ITEM_HEIGHT = 61;
  readonly LicenseState = LicenseState;
  readonly UserAdminState = UserAdminState;

  readonly actionButtonsByState = computed<Record<UserAdminState, ActionButton[]> | undefined>(() => {
    const actionsByUserState = this.actionsByUserState();
    if (!actionsByUserState) {
      return undefined;
    }
    return Object.entries(actionsByUserState).reduce(
      (acc, [state, actions]) => ({
        ...acc,
        [state]: menuActionsToActionsButtons(actions),
      }),
      {} as Record<UserAdminState, ActionButton[]>,
    );
  });

  adminChange(user: AdministrationUser): void {
    this.adminChanged.emit(user);
  }

  trackByUser(index: number, item: AdministrationUser): string {
    return item.id ? item.id.toString() : item.mailAddress;
  }
}
